import { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  FileText,
  Users,
  Package,
  Settings,
  ChevronLeft,
  LogOut,
  UserCircle,
  X,
  Lightbulb,
  Bug,
  Store,
  ShieldCheck,
  Inbox,
  Paperclip,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth.js';
import { useSidebar } from '../hooks/useSidebar.js';
import { useEntityLabels } from '../hooks/useEntityLabels.js';

interface NavItem {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  to: string;
  roles?: string[];
}

export function Sidebar() {
  const { user, logout } = useAuth();
  const { collapsed, toggleCollapsed, mobileOpen, setMobileOpen } = useSidebar();
  const labels = useEntityLabels();
  const navigate = useNavigate();

  const [popoverOpen, setPopoverOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!popoverOpen) return;
    const handler = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setPopoverOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [popoverOpen]);

  const mainNavItems: NavItem[] = [
    { label: 'Dashboard', icon: LayoutDashboard, to: '/' },
    { label: 'Quotes', icon: FileText, to: '/quotes' },
    ...(user?.tenantType === 'reseller'
      ? [{ label: 'Incoming Quotes', icon: Inbox, to: '/incoming-quotes' } as NavItem]
      : []),
    { label: labels.Resellers, icon: Users, to: '/resellers' },
    { label: 'Catalogs', icon: Package, to: '/catalogs' },
    { label: 'Marketplace', icon: Store, to: '/marketplace' },
    ...(user?.tenantType === 'manufacturer'
      ? [{ label: 'Collateral', icon: Paperclip, to: '/collateral' } as NavItem]
      : []),
  ];

  const utilityNavItems: NavItem[] = [
    { label: 'Ideas', icon: Lightbulb, to: '/ideas' },
    { label: 'Bug Reports', icon: Bug, to: '/bugs' },
    { label: 'Settings', icon: Settings, to: '/settings', roles: ['owner'] },
    { label: 'Team', icon: Users, to: '/users', roles: ['owner', 'admin'] },
    { label: 'Marketplace Admin', icon: ShieldCheck, to: '/marketplace-admin', roles: ['owner'] },
    ...(user?.email === 'admin@openware.pro'
      ? [{ label: 'Approvals', icon: ShieldCheck, to: '/admin/associations' } as NavItem]
      : []),
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleNavClick = () => {
    if (mobileOpen) {
      setMobileOpen(false);
    }
  };

  const filteredMainItems = mainNavItems.filter(
    (item) => !item.roles || item.roles.includes(user?.role ?? ''),
  );
  const filteredUtilityItems = utilityNavItems.filter(
    (item) => !item.roles || item.roles.includes(user?.role ?? ''),
  );

  const sidebarContent = (
    <div className="flex h-full flex-col">
      {/* Brand */}
      <div className="flex h-14 items-center justify-between border-b border-gray-800 px-4">
        {collapsed ? (
          <img src="/ologo/ologo-64.png" alt="OQ" className="h-8 w-8" />
        ) : (
          <div className="flex items-center gap-2">
            <img src="/ologo/ologo-64.png" alt="" className="h-8 w-8" />
            <span className="text-lg font-bold">
              <span className="text-white">Open</span>
              <span className="text-brand-400">Quote Pro</span>
            </span>
          </div>
        )}
        {/* Collapse toggle — desktop only */}
        <button
          onClick={toggleCollapsed}
          className="hidden rounded-lg p-1 text-gray-400 hover:bg-gray-800 hover:text-gray-200 lg:block"
        >
          <ChevronLeft
            className={`h-5 w-5 transition-transform ${collapsed ? 'rotate-180' : ''}`}
          />
        </button>
      </div>

      {/* Navigation — main items only */}
      <nav className="flex-1 overflow-y-auto px-2 py-3">
        <div className="space-y-1">
          {filteredMainItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              onClick={handleNavClick}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-gray-800 text-brand-400'
                    : 'text-gray-400 hover:bg-gray-800 hover:text-gray-200'
                } ${collapsed ? 'justify-center' : ''}`
              }
            >
              <item.icon className="h-5 w-5 flex-shrink-0" />
              {!collapsed && <span>{item.label}</span>}
            </NavLink>
          ))}
        </div>
      </nav>

      {/* User Section */}
      <div className="border-t border-gray-800 p-3">
        {collapsed ? (
          <div className="relative flex flex-col items-center gap-2" ref={popoverRef}>
            <button
              onClick={() => setPopoverOpen(!popoverOpen)}
              className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-600 text-sm font-medium text-white hover:bg-brand-700"
              title="Menu"
            >
              {user?.name?.charAt(0).toUpperCase()}
            </button>

            {popoverOpen && (
              <div className="absolute bottom-full left-12 z-50 mb-2 w-48 rounded-lg border border-gray-700 bg-gray-800 py-2 shadow-xl">
                {/* User info */}
                <div className="border-b border-gray-700 px-3 pb-2">
                  <p className="truncate text-sm font-medium text-gray-200">{user?.name}</p>
                  <p className="truncate text-xs text-gray-500">{user?.tenant?.name}</p>
                </div>

                {/* Utility items */}
                {filteredUtilityItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={() => {
                      handleNavClick();
                      setPopoverOpen(false);
                    }}
                    className={({ isActive }) =>
                      `flex items-center gap-2 px-3 py-1.5 text-sm ${
                        isActive
                          ? 'text-brand-400'
                          : 'text-gray-400 hover:bg-gray-700 hover:text-gray-200'
                      }`
                    }
                  >
                    <item.icon className="h-4 w-4" />
                    <span>{item.label}</span>
                  </NavLink>
                ))}

                <div className="my-1 border-t border-gray-700" />

                {/* Account + Sign Out */}
                <NavLink
                  to="/account"
                  onClick={() => {
                    handleNavClick();
                    setPopoverOpen(false);
                  }}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-400 hover:bg-gray-700 hover:text-gray-200"
                >
                  <UserCircle className="h-4 w-4" />
                  <span>Account</span>
                </NavLink>
                <button
                  onClick={() => {
                    handleLogout();
                    setPopoverOpen(false);
                  }}
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-sm text-gray-400 hover:bg-gray-700 hover:text-gray-200"
                >
                  <LogOut className="h-4 w-4" />
                  <span>Sign Out</span>
                </button>
              </div>
            )}
          </div>
        ) : (
          <>
            {/* User info */}
            <div className="mb-2">
              <p className="truncate text-sm font-medium text-gray-200">{user?.name}</p>
              <p className="truncate text-xs text-gray-500">{user?.email}</p>
              <div className="mt-1 flex items-center gap-2">
                <span className="truncate text-xs text-gray-400">{user?.tenant?.name}</span>
                <span className="rounded-full bg-gray-800 px-2 py-0.5 text-xs text-gray-400">
                  {user?.tenant?.plan}
                </span>
                <span className="rounded-full bg-gray-800 px-2 py-0.5 text-xs text-gray-400">
                  {user?.tenantType === 'reseller' ? 'Reseller' : 'Mfr'}
                </span>
              </div>
            </div>

            {/* Utility items — same style as Account/Sign Out */}
            {filteredUtilityItems.length > 0 && (
              <>
                <div className="my-2 border-t border-gray-800" />
                {filteredUtilityItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={handleNavClick}
                    className={({ isActive }) =>
                      `mb-0.5 flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-sm ${
                        isActive
                          ? 'text-brand-400'
                          : 'text-gray-400 hover:bg-gray-800 hover:text-gray-200'
                      }`
                    }
                  >
                    <item.icon className="h-4 w-4" />
                    <span>{item.label}</span>
                  </NavLink>
                ))}
              </>
            )}

            {/* Account + Sign Out */}
            <div className="my-2 border-t border-gray-800" />
            <NavLink
              to="/account"
              onClick={handleNavClick}
              className={({ isActive }) =>
                `mb-0.5 flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-sm ${
                  isActive
                    ? 'text-brand-400'
                    : 'text-gray-400 hover:bg-gray-800 hover:text-gray-200'
                }`
              }
            >
              <UserCircle className="h-4 w-4" />
              <span>Account</span>
            </NavLink>
            <button
              onClick={handleLogout}
              className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-gray-400 hover:bg-gray-800 hover:text-gray-200"
            >
              <LogOut className="h-4 w-4" />
              <span>Sign Out</span>
            </button>
          </>
        )}
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-30 hidden border-r border-gray-800 bg-gray-950 transition-all duration-200 lg:block ${
          collapsed ? 'w-16' : 'w-60'
        }`}
      >
        {sidebarContent}
      </aside>

      {/* Mobile Overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div
            className="fixed inset-0 bg-black/60"
            onClick={() => setMobileOpen(false)}
          />
          <aside className="fixed inset-y-0 left-0 z-50 w-60 border-r border-gray-800 bg-gray-950">
            <button
              onClick={() => setMobileOpen(false)}
              className="absolute right-2 top-3 rounded-lg p-1 text-gray-400 hover:text-gray-200"
            >
              <X className="h-5 w-5" />
            </button>
            {sidebarContent}
          </aside>
        </div>
      )}
    </>
  );
}
